import React, { useEffect, useRef, useState } from 'react';
import * as ReactDOM from 'react-dom';
import { useFieldArray, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import {
  Alert, Button, Col, Icons, Row,
} from '../../../components/base/BootstrapComponents/BootstrapComponents';
import { errorNotification, successNotification } from '../../Notifications/notificationDecorator';
import {
  getResponseError, isCoordinatePair, responseLogWithNotification, setFormError,
} from '../../../utils/utils';
import { FORM_VALIDATION_MESSAGE } from '../../../utils/apiError';
import { createViolation, editViolationById } from '../actions';
import Loader from '../../../components/base/Loader/Loader';
import { getViolationData } from '../reducer';
import ViolationDTO from '../../../dto/ViolationDTO';
import FieldDTOWithCol from '../../../components/fields/Abstract/FieldDTOWithCol';
import TextInputWithLabel from '../../../components/fields/TextInput/TextInputWithLabel';
import CustomSelectInput from '../../../components/fields/SelectInput/CustomSelectInput';
import {
  getViolationStatusDisplayValue,
  ROLE_TYPE,
  VIOLATION_STATUS,
  VIOLATION_STATUS_LIST,
  VIOLATION_TYPE_LIST,
} from '../../../configs';
import { loadUserList } from '../../User/actions';
import { getUserData } from '../../User/reducer';
import { loadProviderList } from '../../Provider/actions';
import { getProviderData } from '../../Provider/reducer';
import { loadVehiclesList } from '../../Agency/actions';
import { getVehiclesData } from '../../Agency/reducer';
import YMap from '../../../components/base/Map/YMap';
import ViolationGallery from './ViolationGallery';
import { getPlacemark } from '../../../components/base/Map/mapUtils';
import { infoFormatter, localeFormatter } from '../../../utils/formatters';
import FileInput from '../../../components/fields/FileInput/FileInput';

const LOCATION_HELP_TEXT = 'Формат: широта, долгота. Пример: 55.7558, 37.6173. Первое число — широта, второе — долгота. Можно выбрать точку кликом на карте.';
const LOCATION_REQUIRED_ERROR = 'Укажите координаты в формате: широта, долгота. Например: 55.7558, 37.6173.';
const LOCATION_INVALID_ERROR = 'Некорректные координаты. Введите два числа через запятую: сначала широту, затем долготу.';
const PROVIDER_REQUIRED_ERROR = 'Выберите провайдера нарушения.';

const parseLocationInput = value => {
  const rawValue = String(value || '').trim();
  if (!rawValue) {
    return { isValid: false, message: LOCATION_REQUIRED_ERROR };
  }

  const parts = rawValue.split(',').map(part => part.trim());
  if (parts.length !== 2 || !parts[0] || !parts[1]) {
    return { isValid: false, message: LOCATION_INVALID_ERROR };
  }

  const latitude = Number(parts[0]);
  const longitude = Number(parts[1]);
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
    return { isValid: false, message: LOCATION_INVALID_ERROR };
  }

  if (latitude < -90 || latitude > 90) {
    return { isValid: false, message: 'Широта должна быть числом от -90 до 90.' };
  }

  if (longitude < -180 || longitude > 180) {
    return { isValid: false, message: 'Долгота должна быть числом от -180 до 180.' };
  }

  return {
    isValid: true,
    latitude,
    longitude,
    geoJson: {
      type: 'Point',
      coordinates: [longitude, latitude],
    },
  };
};

const formatLocationInput = (latitude, longitude) => `${Number(latitude).toFixed(6)}, ${Number(longitude).toFixed(6)}`;

const getLocationInputFromViolation = violation => {
  const coordinates = violation?.location?.coordinates;
  if (!isCoordinatePair(coordinates)) {
    return '';
  }

  return formatLocationInput(coordinates[1], coordinates[0]);
};

const getViolationFormDefaults = violation => ({
  status: getViolationStatusDisplayValue(violation?.status || VIOLATION_STATUS.CREATED),
  violation_type: violation?.violation_type || 'parking',
  vehicle_id: violation?.vehicle_id || '',
  provider_id: violation?.provider_id || '',
  inspector_id: violation?.inspector_id || '',
  description: violation?.description || '',
  location: getLocationInputFromViolation(violation),
  photos: [],
});

const withCurrentOption = (options, value, name) => {
  if (!value || options.some(option => String(option.value) === String(value))) {
    return options;
  }

  return [{ value, name: name || value }, ...options];
};

export default function ViolationForm(props) {
  const {
    onClose = () => {},
    loadData = () => {},
    loadViolation = () => {},
    currentViolation = {},
    isReadOnly = false,
  } = props;
  const [mapInstance, setMapInstance] = useState(null);
  const selectedLocationPlacemarkRef = useRef(null);
  const { loadingCreateViolation, loadingEditViolationById } = useSelector(getViolationData);
  const { providerList = [], loadingProviderList } = useSelector(getProviderData);
  const { vehiclesList = [], loadingVehiclesList } = useSelector(getVehiclesData);
  const { userList = [], loadingUserList } = useSelector(getUserData);
  const dispatch = useDispatch();
  const isCreate = !currentViolation?.uuid;
  const canEditForm = !isReadOnly;

  const { photo_url = [], photo_error: photoError } = currentViolation;

  const {
    reset, control, getValues, setError, setValue, clearErrors, watch,
  } = useForm({
    mode: 'onChange',
    defaultValues: getViolationFormDefaults(currentViolation),
  });
  const locationValue = watch('location');

  const updateSelectedLocationMarker = (latitude, longitude, shouldCenter = false) => {
    if (!mapInstance || !window.ymaps) {
      return;
    }

    const coordinates = [latitude, longitude];
    if (selectedLocationPlacemarkRef.current) {
      selectedLocationPlacemarkRef.current.geometry.setCoordinates(coordinates);
    } else {
      selectedLocationPlacemarkRef.current = new window.ymaps.Placemark(coordinates, {}, {
        preset: 'islands#redDotIcon',
      });
      mapInstance.geoObjects.add(selectedLocationPlacemarkRef.current);
    }

    if (shouldCenter) {
      mapInstance.setCenter(coordinates);
      mapInstance.setZoom(16);
    }
  };

  const validateLocationField = value => {
    const parsedLocation = parseLocationInput(value);
    if (!parsedLocation.isValid) {
      setError('location', { type: 'manual', message: parsedLocation.message }, { shouldFocus: true });
      return parsedLocation;
    }

    clearErrors('location');
    return parsedLocation;
  };

  const handleSave = () => {
    if (!canEditForm) {
      return;
    }
    const values = getValues();
    if (isCreate && (!values.photos || !values.photos.length)) {
      setError('photos', { type: 'manual', message: 'Добавьте хотя бы одно фото' }, { shouldFocus: true });
      errorNotification('Сохранение формы', FORM_VALIDATION_MESSAGE);
      return;
    }

    let payload = values;
    if (isCreate) {
      if (!values.provider_id) {
        setError('provider_id', { type: 'manual', message: PROVIDER_REQUIRED_ERROR }, { shouldFocus: true });
        errorNotification('Сохранение формы', FORM_VALIDATION_MESSAGE);
        return;
      }
    }

    const parsedLocation = validateLocationField(values.location);
    if (!parsedLocation.isValid) {
      errorNotification('Сохранение формы', FORM_VALIDATION_MESSAGE);
      return;
    }

    payload = {
      ...values,
      location: JSON.stringify(parsedLocation.geoJson),
    };

    if (!payload.vehicle_id) {
      setError('vehicle_id', { type: 'manual', message: 'Выберите транспортное средство' }, { shouldFocus: true });
      errorNotification('Сохранение формы', FORM_VALIDATION_MESSAGE);
      return;
    }

    const action = currentViolation?.uuid ? editViolationById({ ...payload, id: currentViolation.uuid }) : createViolation(payload);
    dispatch(action)
      .then(() => {
        successNotification('Сохранение формы', 'Форма сохранена');
        loadData();
        if (isCreate) {
          onClose();
          return;
        }

        loadViolation(currentViolation.uuid, true);
      })
      .catch(response => {
        setFormError(response, setError);
        errorNotification('Сохранение формы', getResponseError(response));
      });
  };

  React.useEffect(() => {
    reset(getViolationFormDefaults(currentViolation));
  }, [currentViolation, reset]);

  useEffect(() => {
    if (!canEditForm) {
      return;
    }

    dispatch(loadProviderList({ limit: 100, page: 1 }))
      .catch(response => {
        errorNotification('Загрузка провайдеров', getResponseError(response));
      });
    dispatch(loadVehiclesList({ limit: 100, page: 1 }))
      .catch(response => {
        errorNotification('Загрузка ТС', getResponseError(response));
      });
    dispatch(loadUserList({ limit: 100, page: 1 }))
      .catch(response => {
        errorNotification('Загрузка пользователей', getResponseError(response));
      });
  }, [dispatch, canEditForm]);

  useEffect(() => {
    if (mapInstance && isCoordinatePair(currentViolation?.location?.coordinates)) {
      const placemark = getPlacemark(currentViolation);
      if (placemark) {
        mapInstance.geoObjects.add(placemark);
      }
      mapInstance.setZoom(16);
      mapInstance.setCenter([currentViolation.location.coordinates[1], currentViolation.location.coordinates[0]]);
      // mapInstance.setCenter(currentViolation.location.coordinates);
    }
  }, [currentViolation, mapInstance]);

  useEffect(() => {
    if (!canEditForm || !mapInstance || !window.ymaps) {
      return undefined;
    }

    const handleMapClick = event => {
      const coordinates = event.get('coords');
      if (!Array.isArray(coordinates) || coordinates.length < 2) {
        return;
      }

      const [latitude, longitude] = coordinates;
      setValue('location', formatLocationInput(latitude, longitude), {
        shouldDirty: true,
        shouldValidate: true,
      });
      clearErrors('location');
      updateSelectedLocationMarker(latitude, longitude, true);
    };

    mapInstance.events.add('click', handleMapClick);
    return () => {
      mapInstance.events.remove('click', handleMapClick);
    };
  }, [canEditForm, mapInstance]);

  useEffect(() => {
    if (!canEditForm) {
      return;
    }

    const rawValue = String(locationValue || '').trim();
    if (!rawValue) {
      clearErrors('location');
      return;
    }

    const parsedLocation = parseLocationInput(locationValue);
    if (!parsedLocation.isValid) {
      setError('location', { type: 'manual', message: parsedLocation.message });
      return;
    }

    clearErrors('location');
    updateSelectedLocationMarker(parsedLocation.latitude, parsedLocation.longitude);
  }, [locationValue, canEditForm, mapInstance]);

  const dto = new ViolationDTO();
  const attributes = dto.getAttributesByName();
  const providerOptions = withCurrentOption((providerList || []).map(provider => ({
    value: provider.id,
    name: provider.name,
  })), currentViolation?.provider_id, currentViolation?.provider_name || currentViolation?.provider_id);
  const vehicleOptions = withCurrentOption((vehiclesList || []).map(vehicle => ({
    value: vehicle.vehicle_id,
    name: `${vehicle.vehicle_id}${vehicle.provider_name ? ` (${vehicle.provider_name})` : ''}`,
  })), currentViolation?.vehicle_id, currentViolation?.vehicle_id);
  const inspectorOptions = withCurrentOption((userList || [])
    .filter(user => Number(user.role) === ROLE_TYPE.INSPECTOR || String(user.id) === String(currentViolation?.inspector_id))
    .map(user => ({
      value: user.id,
      name: user.full_name ? `${user.full_name} (${user.username})` : user.username || user.id,
    })), currentViolation?.inspector_id, currentViolation?.inspector_login || currentViolation?.inspector_id);

  return ReactDOM.createPortal(
    <div className="form-dialog violation-form">
      <Loader loading={loadingCreateViolation || loadingEditViolationById} />
      <div className="form-header">
        <div className="breadcrumbs">
          <span className="breadcrumbs-prev">Нарушения</span>
          <Icons width={22} height={22} customIcon icon="arrow-right" className="breadcrumbs-arrow" />
          <span className="breadcrumbs-current">Нарушение № {currentViolation?.uuid || 'Без номера'}</span>
          <span className="breadcrumbs-current"> ({currentViolation?.uuid ? (isReadOnly ? 'просмотр' : 'редактирование') : 'создание'}) </span>

        </div>
        <div className="close-button" onClick={onClose}>
          <Icons width={24} height={24} customIcon icon="close" />
        </div>
      </div>
      <div className="form-body">
        <form className="form-content">
          <div className="form-fields">
            <div className="form-fields-wrapper custom-scroll-theme">
              <div className="afc-body">
                <div className="afc-main violation-main-info">
                  <div className="f4">Основная информация</div>
                  {!isCreate ? (
                    <>
                      <Row>
                        <Col columns={12}>
                          {infoFormatter('ИД нарушения', currentViolation.uuid)}
                        </Col>
                      </Row>
                      {!isReadOnly ? (
                        <>
                          <Row>
                            <FieldDTOWithCol
                              control={control}
                              component={CustomSelectInput}
                              dtoObject={attributes.vehicle_id}
                              variant="material"
                              withLabel
                              columns={4}
                              options={vehicleOptions}
                              addPlaceholder
                              placeholder={loadingVehiclesList ? 'Загрузка ТС...' : 'Выберите ТС'}
                              disabled={loadingVehiclesList || !vehicleOptions.length}
                            />
                            <FieldDTOWithCol
                              control={control}
                              component={CustomSelectInput}
                              dtoObject={attributes.inspector_id}
                              variant="material"
                              withLabel
                              columns={4}
                              options={inspectorOptions}
                              addPlaceholder
                              placeholder={loadingUserList ? 'Загрузка инспекторов...' : 'Выберите инспектора'}
                              disabled={loadingUserList || !inspectorOptions.length}
                            />
                            <FieldDTOWithCol
                              control={control}
                              component={CustomSelectInput}
                              dtoObject={attributes.provider_id}
                              variant="material"
                              withLabel
                              columns={4}
                              options={providerOptions}
                              addPlaceholder
                              placeholder={loadingProviderList ? 'Загрузка провайдеров...' : 'Выберите провайдера'}
                              textMuted="Провайдер должен соответствовать выбранному транспортному средству."
                              disabled={loadingProviderList || !providerOptions.length}
                            />
                          </Row>
                          <Row>
                            <FieldDTOWithCol
                              control={control}
                              component={CustomSelectInput}
                              dtoObject={attributes.violation_type}
                              variant="material"
                              withLabel
                              columns={6}
                              options={VIOLATION_TYPE_LIST}
                            />
                            <FieldDTOWithCol
                              control={control}
                              type="text"
                              component={TextInputWithLabel}
                              dtoObject={attributes.location}
                              variant="material"
                              withLabel
                              columns={6}
                              textMuted={LOCATION_HELP_TEXT}
                              placeholder="55.7558, 37.6173"
                            />
                          </Row>
                          <Row>
                            <FieldDTOWithCol
                              control={control}
                              type="text"
                              component={TextInputWithLabel}
                              dtoObject={attributes.description}
                              variant="material"
                              withLabel
                              textarea
                            />
                          </Row>
                          <Row>
                            <FieldDTOWithCol
                              control={control}
                              type="text"
                              component={CustomSelectInput}
                              dtoObject={attributes.status}
                              variant="material"
                              withLabel
                              options={VIOLATION_STATUS_LIST}
                            />
                          </Row>
                          <Row>
                            <FieldDTOWithCol
                              control={control}
                              component={FileInput}
                              dtoObject={attributes.photos}
                              variant="material"
                              withLabel
                              multiple
                              textMuted="Если выбрать новые фото, они заменят текущий набор изображений."
                              inputProps={{
                                accept: 'image/*',
                              }}
                            />
                          </Row>
                        </>
                      ) : (
                        <>
                          <Row>
                            <Col columns={5}>
                              {infoFormatter('ИД ТС', currentViolation.vehicle_id)}
                            </Col>
                            <Col columns={4}>
                              {infoFormatter('ИД инспектора', currentViolation.inspector_id)}
                            </Col>
                            <Col columns={3}>
                              {infoFormatter('Провайдер', currentViolation.provider_name)}
                            </Col>
                          </Row>
                          <Row>
                            <Col columns={6}>
                              {infoFormatter('Тип', localeFormatter('violation.type', currentViolation.violation_type))}
                            </Col>
                            <Col columns={6}>
                              {infoFormatter('Координаты', (Array.isArray(currentViolation?.location?.coordinates) ? currentViolation?.location?.coordinates.join(', ') : ''))}
                            </Col>
                          </Row>
                          <Row>
                            <Col columns={12}>
                              {infoFormatter('Описание', currentViolation.description)}
                            </Col>
                          </Row>
                          <Row>
                            <Col columns={6}>
                              {infoFormatter('Статус', localeFormatter('violation.status', getViolationStatusDisplayValue(currentViolation?.status)))}
                            </Col>
                          </Row>
                        </>
                      )}
                    </>
                  ) : (
                    <>
                      <Row>
                        <FieldDTOWithCol
                          control={control}
                          type="text"
                          component={TextInputWithLabel}
                          dtoObject={attributes.vehicle_id}
                          variant="material"
                          withLabel
                          columns={7}
                        />
                        <FieldDTOWithCol
                          control={control}
                          component={CustomSelectInput}
                          dtoObject={attributes.provider_id}
                          variant="material"
                          withLabel
                          columns={5}
                          options={providerOptions}
                          addPlaceholder
                          placeholder={loadingProviderList ? 'Загрузка провайдеров...' : 'Выберите провайдера'}
                          textMuted="Провайдер должен соответствовать выбранному транспортному средству."
                          disabled={loadingProviderList || !providerOptions.length}
                        />
                      </Row>
                      <Row>
                        <FieldDTOWithCol
                          control={control}
                          component={CustomSelectInput}
                          dtoObject={attributes.violation_type}
                          variant="material"
                          withLabel
                          options={VIOLATION_TYPE_LIST}
                        />
                      </Row>
                      <Row>
                        <Col columns={6}>
                          {infoFormatter('Статус', localeFormatter('violation.status', VIOLATION_STATUS.CREATED))}
                        </Col>
                        <FieldDTOWithCol
                          control={control}
                          type="text"
                          component={TextInputWithLabel}
                          dtoObject={attributes.location}
                          variant="material"
                          withLabel
                          textMuted={LOCATION_HELP_TEXT}
                          placeholder="55.7558, 37.6173"
                        />
                      </Row>
                      <Row>
                        <FieldDTOWithCol
                          control={control}
                          type="text"
                          component={TextInputWithLabel}
                          dtoObject={attributes.description}
                          variant="material"
                          withLabel
                          textarea
                        />
                      </Row>
                      <Row>
                        <FieldDTOWithCol
                          control={control}
                          component={FileInput}
                          dtoObject={attributes.photos}
                          variant="material"
                          withLabel
                          multiple
                          textMuted="Добавьте 1-3 изображения. Обязательно минимум 1 фото."
                          inputProps={{
                            accept: 'image/*',
                          }}
                        />
                      </Row>
                    </>
                  )}
                </div>
                <div className="afc-add">
                  <div className="f4">Месторасположение</div>
                  <div className="violation-map-box">
                    <YMap
                      className="violation-map"
                      setMapInstance={i => setMapInstance(i)}
                    />
                  </div>
                  <div className="f4 mt-3">Фото</div>
                  {photoError ? <Alert className="mt-3" variant="warning">{photoError}</Alert> : null}
                  <ViolationGallery className="mt-3" photos={photo_url} />
                </div>
              </div>
            </div>
          </div>
          <div className="form-footer">
            {!isReadOnly ? (
              <Button onClick={() => handleSave()}>{currentViolation?.uuid ? 'Сохранить' : 'Создать'}</Button>
            ) : null}
            <Button variant="secondary" onClick={onClose}>Отменить</Button>
          </div>
        </form>
      </div>
    </div>,
    document.getElementById('app-content'),
  );
}
